/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DAD.EXPLORER — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DadExplorerPage.tsx. A DISTINCT
 * presentation (KPI tiles + searchable card list + bottom-sheet schema detail)
 * over the SAME data layer only:
 *   - reads the documented entity catalog via apiDb (dad_documented_entity), and
 *   - reflects each entity's live schema via the dad_entity_columns RPC (real +
 *     custom columns), exactly like the desktop explorer.
 *
 * READ-ONLY by design — this is the browse surface of the API docs. The desktop
 * sandbox's write path (dry-run / execute-for-real, OpenAPI download) is NOT
 * re-hosted here: no mutation ever leaves this screen, so it never imports the
 * Common Routine (routed_writes). §16: a purpose-built mobile screen, never the
 * desktop grid re-rendered in a phone viewport.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Database, Hash, X, Loader } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { asfWorkspaceCode } from '../../lib/asf';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow,
  MobileFilterChips, MobileStatusPill,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ---------------------------------------------------------------------------
// Data shapes — mirror the desktop DadExplorerPage reads.
// ---------------------------------------------------------------------------

interface EntityRow {
  entity_name: string;
  title: string | null;
  allowed_kinds: string[];
}

interface ColRow {
  name: string;
  type: string;
  format?: string;
  required?: boolean;
  custom?: boolean;
  db_type?: string;
}

type RawEntityRow = { entity_name: string; title: string | null; allowed_kinds: unknown };
type CapFacet = 'all' | 'read' | 'write';

// Operation kinds are a fixed CHECK-enum-style vocabulary — label them through
// i18n via a labelled constant (§17.B11), never a bare stored literal.
const OP_LABELS: { value: string; labelKey: string; fallback: string }[] = [
  { value: 'select', labelKey: 'mobile.dad.op.select', fallback: 'Read' },
  { value: 'insert', labelKey: 'mobile.dad.op.insert', fallback: 'Create' },
  { value: 'update', labelKey: 'mobile.dad.op.update', fallback: 'Update' },
  { value: 'delete', labelKey: 'mobile.dad.op.delete', fallback: 'Delete' },
];

function normalizeKinds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return ['select'];
  const kinds = raw.filter((k): k is string => typeof k === 'string');
  return kinds.length > 0 ? kinds : ['select'];
}

/** An entity is "writable" when any non-read operation is documented for it. */
function isWritable(kinds: string[]): boolean {
  return kinds.some((k) => k !== 'select');
}

export function MobileDadExplorerPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<EntityRow[]>([]);
  const [filter, setFilter] = useState('');
  const [capFacet, setCapFacet] = useState<CapFacet>('all');
  const [selected, setSelected] = useState<EntityRow | null>(null);
  const [cols, setCols] = useState<ColRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [colsBusy, setColsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setBusy(true);
      setError(null);
      try {
        const { data, error: e } = await apiDb
          .from('dad_documented_entity')
          .select('entity_name, title, allowed_kinds')
          .eq('is_active', 'Y')
          .order('sort_order')
          .order('entity_name');
        if (e) throw e;
        const rows: EntityRow[] = ((data ?? []) as RawEntityRow[]).map((r) => ({
          entity_name: r.entity_name,
          title: r.title,
          allowed_kinds: normalizeKinds(r.allowed_kinds),
        }));
        setEntities(rows);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  const openEntity = async (entity: EntityRow) => {
    setSelected(entity);
    setCols([]);
    setColsBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<ColRow[]>('dad_entity_columns', {
        p_entity: entity.entity_name,
        p_workspace: asfWorkspaceCode(),
      });
      if (e) throw e;
      setCols(Array.isArray(data) ? data : []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setColsBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return entities.filter((en) => {
      const writable = isWritable(en.allowed_kinds);
      if (capFacet === 'read' && writable) return false;
      if (capFacet === 'write' && !writable) return false;
      if (!q) return true;
      return (
        en.entity_name.toLowerCase().includes(q) ||
        (en.title ?? '').toLowerCase().includes(q)
      );
    });
  }, [entities, filter, capFacet]);

  const kpis = useMemo(() => {
    let write = 0;
    for (const en of entities) if (isWritable(en.allowed_kinds)) write += 1;
    return { total: entities.length, write, read: entities.length - write };
  }, [entities]);

  const facets: { key: CapFacet; label: string }[] = [
    { key: 'all', label: t('mobile.dad.facet.all', 'All') },
    { key: 'read', label: t('mobile.dad.facet.read', 'Read-only') },
    { key: 'write', label: t('mobile.dad.facet.write', 'Writable') },
  ];

  const opLabel = (kind: string): string => {
    const def = OP_LABELS.find((o) => o.value === kind);
    return def ? t(def.labelKey, def.fallback) : kind;
  };

  const sourceLabel = (custom?: boolean): string =>
    custom
      ? t('mobile.dad.source.custom', 'Custom field')
      : t('mobile.dad.source.column', 'Table column');

  const capLabel = (writable: boolean): string =>
    writable ? t('mobile.dad.cap.write', 'Read/Write') : t('mobile.dad.cap.read', 'Read-only');

  const titleOf = (en: EntityRow): string =>
    en.title && en.title !== en.entity_name ? en.title : en.entity_name;

  const opsSummary = (en: EntityRow): string =>
    en.allowed_kinds.map(opLabel).join(' · ');

  const requiredCount = useMemo(() => cols.filter((c) => c.required).length, [cols]);
  const customCount = useMemo(() => cols.filter((c) => c.custom).length, [cols]);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.dad.title', 'API Explorer')}
        description={t('mobile.dad.description', 'Documented entities and their live reflected schema.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.total, label: t('mobile.dad.kpi.entities', 'Entities') },
          { value: kpis.write, label: t('mobile.dad.kpi.writable', 'Writable') },
          { value: kpis.read, label: t('mobile.dad.kpi.read_only', 'Read-only') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.dad.search', 'Search entities')}
          aria-label={t('mobile.dad.search', 'Search entities')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => capFacet === v}
        onSelect={(v) => setCapFacet(v as CapFacet)}
      />

      {error && !selected && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dad.none', 'No entities match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((en) => {
            const writable = isWritable(en.allowed_kinds);
            return (
              <MobileCardRow
                key={en.entity_name}
                lead={<Database />}
                leadTone={writable ? 'amber' : 'slate'}
                title={titleOf(en)}
                subtitle={<><span className="m-code">{en.entity_name}</span> · {opsSummary(en)}</>}
                trailing={<MobileStatusPill tone={writable ? 'info' : 'ok'}>{capLabel(writable)}</MobileStatusPill>}
                chevron
                onClick={() => void openEntity(en)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet — the entity's live reflected schema */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.dad.detail', 'Entity schema')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Database size={20} /></span>
              <span>
                <div className="m-nm">{titleOf(selected)}</div>
                <div className="m-em">{selected.entity_name}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.dad.operations', 'Operations')}
                value={
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    {selected.allowed_kinds.map((k) => (
                      <MobileStatusPill key={k} tone={k === 'select' ? 'ok' : 'info'}>{opLabel(k)}</MobileStatusPill>
                    ))}
                  </span>
                }
              />
              <MobileDetailRow label={t('mobile.dad.columns', 'Columns')} value={cols.length} />
              <MobileDetailRow label={t('mobile.dad.required', 'Required')} value={requiredCount} />
              <MobileDetailRow label={t('mobile.dad.custom', 'Custom fields')} value={customCount} />
            </MobileContentCard>

            {error && (
              <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></MobileContentCard>
            )}

            {colsBusy ? (
              <MobileContentCard><div className="m-empty"><Loader /> {t('mobile.dad.loading', 'Reflecting schema…')}</div></MobileContentCard>
            ) : cols.length === 0 && !error ? (
              <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dad.no_columns', 'No columns reflected')}</div></MobileContentCard>
            ) : (
              <MobileContentCard list>
                {cols.map((c) => (
                  <MobileCardRow
                    key={c.name}
                    lead={<Hash />}
                    leadTone={c.custom ? 'amber' : 'slate'}
                    title={<span className="m-code">{c.name}</span>}
                    subtitle={`${c.type}${c.format ? ` · ${c.format}` : ''} · ${sourceLabel(c.custom)}`}
                    trailing={c.required ? <MobileStatusPill tone="wait">{t('mobile.dad.required', 'Required')}</MobileStatusPill> : undefined}
                  />
                ))}
              </MobileContentCard>
            )}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('mobile.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
